import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react'
import apiServices from '../libs/services'
import { HTTP_STATUS, PreferenceType } from '../enums/enums'
import { useAuth } from '../hooks/useAuth'

interface UserPreferencesContextValue {
  preferenceA11yEad: boolean,
  preferenceA11yMcq: boolean,
  preferenceA11yReset: boolean,
  preferenceA11yBgm: boolean,
  preferenceA11yCc: boolean,
  preferenceA11yReduceMotion: boolean,
  preferenceAsmVision: boolean,  
  preferenceAsmHearing: boolean,  
  preferenceAsmMotor: boolean,  
  preferenceAsmCognitive: boolean,  

  setPreferenceA11yEad: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceA11yMcq: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceA11yReset: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceA11yBgm: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceA11yCc: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceA11yReduceMotion: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceAsmVision: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceAsmHearing: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceAsmMotor: React.Dispatch<React.SetStateAction<boolean>>,
  setPreferenceAsmCognitive: React.Dispatch<React.SetStateAction<boolean>>,

  isPreferencesLoaded: boolean

  fetchPreferences: () => Promise<void>
}

const UserPreferencesContext = createContext<UserPreferencesContextValue>({
  preferenceA11yEad: false,
  preferenceA11yMcq: false,
  preferenceA11yReset: false,
  preferenceA11yBgm: true,
  preferenceA11yCc: false,
  preferenceA11yReduceMotion: false,
  preferenceAsmVision: false,  
  preferenceAsmHearing: false,  
  preferenceAsmMotor: false,  
  preferenceAsmCognitive: false,  

  setPreferenceA11yEad: () => {} ,
  setPreferenceA11yMcq: () => {} ,
  setPreferenceA11yReset: () => {} ,
  setPreferenceA11yBgm: () => {} ,
  setPreferenceA11yCc: () => {} ,
  setPreferenceA11yReduceMotion: () => {} ,
  setPreferenceAsmVision: () => {} ,
  setPreferenceAsmHearing: () => {} ,
  setPreferenceAsmMotor: () => {} ,
  setPreferenceAsmCognitive: () => {} ,

  isPreferencesLoaded: false,
  fetchPreferences: async () => {}
})

export const UserPreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userProfile } = useAuth()

  const [isPreferencesLoaded, setIsPreferencesLoaded] = useState(false)
  const isFetchingRef = useRef(false)

  // Preferences: accessibility individual settings
  const [preferenceA11yEad, setPreferenceA11yEad] = useState<boolean>(false)
  const [preferenceA11yMcq, setPreferenceA11yMcq] = useState<boolean>(false)
  const [preferenceA11yReset, setPreferenceA11yReset] = useState<boolean>(false) // Only for UI usage, not real
  const [preferenceA11yBgm, setPreferenceA11yBgm] = useState<boolean>(true)
  const [preferenceA11yCc, setPreferenceA11yCc] = useState<boolean>(false)
  const [preferenceA11yReduceMotion, setPreferenceA11yReduceMotion] = useState<boolean>(false)

  // Preferences: accessibility modes
  const [preferenceAsmVision, setPreferenceAsmVision] = useState<boolean>(false)
  const [preferenceAsmHearing, setPreferenceAsmHearing] = useState<boolean>(false)
  const [preferenceAsmMotor, setPreferenceAsmMotor] = useState<boolean>(false)
  const [preferenceAsmCognitive, setPreferenceAsmCognitive] = useState<boolean>(false)

  const fetchPreferences = useCallback(async (): Promise<void> => {
    if (!userProfile) {
      setIsPreferencesLoaded(false)
      return
    }

    isFetchingRef.current = true
    try {
      const res = await apiServices.getUserPreferences()
      if (res.status !== HTTP_STATUS.OK) {
        throw new Error("Preferences fetching failed. This is only an actual error if the user is logged in.")
      }
      const responsePrefs = (res?.data as any)?.userPreferences
      const prefs: any[] = Array.isArray(responsePrefs)
        ? responsePrefs
        : (Array.isArray(res?.data as any) ? (res?.data as any) : [])

      for (const pref of prefs) {
        if (pref?.preferenceType == null || pref?.value == null) {
          continue
        }
        const value = pref.value === 'true'
        switch (pref.preferenceType as PreferenceType) {
          case PreferenceType.A11yUseEad: {
            setPreferenceA11yEad(value)
            break
          }
          case PreferenceType.A11yUseAccessibleMcqGame: {
            setPreferenceA11yMcq(value)
            break
          }
          case PreferenceType.A11yBackgroundMusic: {
            setPreferenceA11yBgm(value)
            break
          }
          case PreferenceType.A11yClosedCaptions: {
            setPreferenceA11yCc(value)
            break
          }
          case PreferenceType.A11yReduceMotion: {
            setPreferenceA11yReduceMotion(value)
            break
          }
          // ASM (Accessibility Support Modes)
          case PreferenceType.AsmVision: {
            setPreferenceAsmVision(value)
            break
          }
          case PreferenceType.AsmHearing: {
            setPreferenceAsmHearing(value)
            break
          }
          case PreferenceType.AsmMotor: {
            setPreferenceAsmMotor(value)
            break
          }
          case PreferenceType.AsmCognitive: {
            setPreferenceAsmCognitive(value)
            break
          }
        }
      }

      setIsPreferencesLoaded(true)
    } catch (_e) {
      // On failure, keep defaults and mark as not loaded
      setIsPreferencesLoaded(false)
    } finally {
      isFetchingRef.current = false
    }
  }, [userProfile])

  useEffect(() => {
    fetchPreferences()
  }, [fetchPreferences])

  // Sync individual preferences to backend when they change
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.A11yUseEad, preferenceA11yEad.toString())
  }, [preferenceA11yEad, isPreferencesLoaded])
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.A11yUseAccessibleMcqGame, preferenceA11yMcq.toString())
  }, [preferenceA11yMcq, isPreferencesLoaded])
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.A11yBackgroundMusic, preferenceA11yBgm.toString())
  }, [preferenceA11yBgm, isPreferencesLoaded])
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.A11yClosedCaptions, preferenceA11yCc.toString())
  }, [preferenceA11yCc, isPreferencesLoaded])
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.A11yReduceMotion, preferenceA11yReduceMotion.toString())
  }, [preferenceA11yReduceMotion, isPreferencesLoaded])

  // Sync ASM preferences to backend when they change
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.AsmVision, preferenceAsmVision.toString())
  }, [preferenceAsmVision, isPreferencesLoaded])
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.AsmHearing, preferenceAsmHearing.toString())
  }, [preferenceAsmHearing, isPreferencesLoaded])
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.AsmMotor, preferenceAsmMotor.toString())
  }, [preferenceAsmMotor, isPreferencesLoaded])
  useEffect(() => {
    if (!isPreferencesLoaded || isFetchingRef.current) return
    void apiServices.setUserPreference(PreferenceType.AsmCognitive, preferenceAsmCognitive.toString())
  }, [preferenceAsmCognitive, isPreferencesLoaded])

  return (
    <UserPreferencesContext.Provider value={{
      preferenceA11yEad,
      preferenceA11yMcq,
      preferenceA11yReset,
      preferenceA11yBgm,
      preferenceA11yCc,
      preferenceA11yReduceMotion,
      preferenceAsmVision,
      preferenceAsmHearing,
      preferenceAsmMotor,
      preferenceAsmCognitive,

      setPreferenceA11yEad,
      setPreferenceA11yMcq,
      setPreferenceA11yReset,
      setPreferenceA11yBgm,
      setPreferenceA11yCc,
      setPreferenceA11yReduceMotion,
      setPreferenceAsmVision,
      setPreferenceAsmHearing,
      setPreferenceAsmMotor,
      setPreferenceAsmCognitive,

      isPreferencesLoaded,

      fetchPreferences
    }}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export const useUserPreferences = (): UserPreferencesContextValue => {
  return useContext(UserPreferencesContext)
}
